import {
  SpecialTabs,
  Tab,
  TabDescriptor,
  TabIDToType,
  tabIDToType,
} from "@store/minersSelect/actionTypes";
import { Select } from "../Select/Select";
import styles from "./MinersTabSelect.module.scss";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { RootState } from "@store";
import { useMe } from "@utils/hooks/useMe";
import { useEffect, useMemo, useState } from "react";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { listTabsRequest } from "@services/vtabs";
import { ScrollableArea } from "../ScrollableArea/ScrollableArea";
import classNames from "classnames";
import connected from "@assets/redesign/icons/connected.svg";
import interArrow from "@assets/redesign/icons/interArrow.svg";
import { MinerTabType } from "@services/http/responses/BaseResponse";
import { useAuth } from "@utils/hooks/useAuth";
import TabsList from "./MinerTabItem";

interface MinersTabSelectProps {
  className?: string;
  label?: React.ReactNode;
  showConnected?: boolean;
  value?: Tab;
  onChange?: { (value: Tab, name: string): void };
};

const groups = [
  {
    name: "machines.table.tabGroups.inactive",
    title: "machines.table.tabGroups.inactive.title",
    tabs: [Tab.OFFLINE, Tab.SERVICE, Tab.STORE],
  },
];

const specialTabsList = (SpecialTabs.filter(tab => {
  for (const group of groups) {
    if (group.tabs.includes(tab.id)) {
      return false;
    }
  }
  return true;
}).map(item => ({
  type: "tab",
  value: item,
})) as ({
  type: "group",
  name: string,
  title: string,
  value: TabDescriptor[],
} | {
  type: "tab",
  value: TabDescriptor,
})[]).concat(groups.map(group => ({
  type: "group",
  name: group.name,
  title: group.title,
  value: group.tabs.map(tabId => SpecialTabs.find(tab => tab.id === tabId)!),
})));

//////

const getTabTotal = (tab: { id: number }, tabsTotals: Record<string, number>, tabIDToType: TabIDToType) => {
  const typeName = tabIDToType(tab.id);
  return typeName ? (tabsTotals[typeName] || 0) : 0;
};

const formatTabLabel = (
  tab: { id: number; name: string },
  tabsTotals: Record<string, number>,
  tabIDToType: TabIDToType,
  formatMessage: (descriptor: { id: string }) => string
) => {
  const total = getTabTotal(tab, tabsTotals, tabIDToType);
  const labelName = tab.id < 0 ? formatMessage({ id: tab.name }) : tab.name;
  return `${labelName} (${total})`;
};

const formatTabTitle = (
  tab: { id: number; name: string; title?: string },
  formatMessage: (descriptor: { id: string }) => string
) => {
  if (tab.id < 0) return formatMessage({ id: tab.title! });
  return tab.title || tab.name;
};

const createTabOption = (
  tab: { id: number; name: string; title?: string },
  tabsTotals: Record<string, number>,
  tabIDToType: TabIDToType, //(id: number) => string,
  formatMessage: (descriptor: { id: string }) => string
) => ({
  label: formatTabLabel(tab, tabsTotals, tabIDToType, formatMessage),
  title: formatTabTitle(tab, formatMessage),
  value: tab.id,
});

const isTabVisible = (tab: { adminOnly?: boolean }, isAdmin: boolean) =>
  isAdmin || !tab.adminOnly;

const isGroupVisible = (group: { value: Array<{ adminOnly?: boolean }> }, isAdmin: boolean) =>
  isAdmin || group.value.some(tab => !tab.adminOnly);

const computeGroupTotal = (
  group: { value: Array<{ id: number; adminOnly?: boolean }> },
  isAdmin: boolean,
  tabsTotals: Record<string, number>,
  tabIDToType: TabIDToType
) =>
  group.value.reduce((sum, tab) => {
    if (isAdmin || !tab.adminOnly) {
      return sum + getTabTotal(tab, tabsTotals, tabIDToType);
    }
    return sum;
  }, 0);

/////

function MinersTabSelect(props: MinersTabSelectProps) {
  const { formatMessage } = useIntl();
  //const me = useMe();
  const auth = useAuth();
  const tabsTotals = useSelector(
    (root: RootState) => root.storedMiners.TabsTotals
  );
  const [tabs, setTabs] = useState<TabDescriptor[]>([]);
  const { request: listTabs } = useTypedRequest(listTabsRequest);

  useEffect(() => {
    if (!auth.isAdmin) return;
    (async () => {
      const r = await listTabs();
      if (!r) return;
      setTabs(r.Records || []);
    })();
  }, [auth.isAdmin]);

  const options = useMemo(() => [
    ...specialTabsList
      .filter(item =>
        (item.type === "tab" && isTabVisible(item.value, auth.isAdmin)) ||
        (item.type === "group" && isGroupVisible(item, auth.isAdmin))
      )
      .map(item => {
        if (item.type === "tab") {
          return createTabOption(item.value, tabsTotals, tabIDToType, formatMessage);
        }
        // item.type === "group"
        const groupTotal = computeGroupTotal(item, auth.isAdmin, tabsTotals, tabIDToType);
        return {
          label: `${formatMessage({ id: item.name })} (${groupTotal})`,
          title: formatMessage({ id: item.title }),
          options: item.value
            .filter(tab => isTabVisible(tab, auth.isAdmin))
            .map(tab => createTabOption(tab, tabsTotals, tabIDToType, formatMessage)),
        };
      }),
    ...tabs.map(tab => createTabOption(tab, tabsTotals, tabIDToType, formatMessage)),
  ], [specialTabsList, tabs, tabsTotals, auth.isAdmin, formatMessage]);

  return (
    <div className={classNames(props.className, styles.minersTabSelect)}>
      <Select
        label={props.label}
        value={props.value}
        onChange={(value) => {
          const name =
            tabs.find((item) => item.id === value)?.name ||
            SpecialTabs.find((item) => item.id === value)?.name;
          props.onChange?.(
            value,
            name ? (value < 0 ? formatMessage({ id: name }) : name) : ""
          );
        }}
        options={options/*specialTabsList.filter((item) => {
          if (item.type === "tab" && (auth.isAdmin || !item.value.adminOnly)) {
            return true;
          } else if (item.type === "group" && (auth.isAdmin || item.value.some(tab => !tab.adminOnly))) {
            return true;
          }
          return false;
        }).map((item) => {
          if (item.type === "tab") {
            const name = tabIDToType(item.value.id);
            return {
              label: `${item.value.id < 0 ? formatMessage({ id: item.value.name }) : item.value.name
                } (${name ? tabsTotals[name] || 0 : 0})`,
              title:
                item.value.id < 0
                  ? formatMessage({ id: item.value.title })
                  : item.value.title || item.value.name,
              value: item.value.id,
            };
          } else {
            return {
              label: `${formatMessage({ id: item.name })} (${item.value.reduce((result, tab) => {
                if (auth.isAdmin || !tab.adminOnly) {
                  const name = tabIDToType(tab.id);
                  return result + (name ? tabsTotals[name] || 0 : 0);
                } else {
                  return result;
                }
              }, 0)
                })`,
              title: formatMessage({ id: item.title }),
              options: item.value.filter(tab => auth.isAdmin || !tab.adminOnly).map(item => {
                const name = tabIDToType(item.id);
                return {
                  label: `${item.id < 0 ? formatMessage({ id: item.name }) : item.name
                    } (${name ? tabsTotals[name] || 0 : 0})`,
                  title:
                    item.id < 0
                      ? formatMessage({ id: item.title })
                      : item.title || item.name,
                  value: item.id,
                };
              }),
            };
          }
        }).concat(
          tabs.map((item) => {
            const name = tabIDToType(item.id);
            return {
              label: `${item.id < 0 ? formatMessage({ id: item.name }) : item.name
                } (${name ? tabsTotals[name] || 0 : 0})`,
              title: `${item.id < 0 ? formatMessage({ id: item.name }) : item.name
                }`,
              value: item.id,
            };
          })
        )*/}
        optionRender={option => {
          if (option.groupOption) {
            return <div className={styles.groupOption}>
              <img src={interArrow} alt="" />
              <div>{option.label}</div>
            </div>
          } else {
            return option.label;
          }
        }}
      />
      {props.showConnected && (
        <div className={styles.connected}>
          <ScrollableArea disableVertical>
            <TabsList
              className={styles.list}
              SpecialTabs={SpecialTabs}
              tabs={tabs}
              onChange={props.onChange}
              value={props.value}
              tabsTotals={tabsTotals}
            />
            {/** 
            <ul className={styles.list}>
              {SpecialTabs.filter((item) => {
                const name = tabIDToType(item.id);
                return (
                  (auth.isAdmin || !item.adminOnly) &&
                  props.value !== item.id &&
                  name &&
                  tabsTotals[name]
                );
              })
                .map((item, i) => {
                  return (
                    <li
                      key={i}
                      title={
                        item.id < 0
                          ? formatMessage({ id: item.title })
                          : item.title || item.name
                      }
                      onClick={() => {
                        props.onChange?.(
                          item.id,
                          item.name
                            ? item.id < 0
                              ? formatMessage({ id: item.name })
                              : item.name
                            : ""
                        );
                      }}
                    >
                      <img src={connected} alt="" />
                      <span>
                        {item.id < 0
                          ? formatMessage({ id: item.name })
                          : item.name}{" "}
                        ({tabsTotals[tabIDToType(item.id)!]})
                      </span>
                    </li>
                  );
                })
                .concat(
                  tabs
                    .filter((item) => {
                      const name = tabIDToType(item.id);
                      return (
                        props.value !== item.id && name && tabsTotals[name]
                      );
                    })
                    .map((item, i) => {
                      return (
                        <li
                          key={i}
                          title={
                            item.id < 0
                              ? formatMessage({ id: item.name })
                              : item.name
                          }
                          onClick={() => {
                            props.onChange?.(
                              item.id,
                              item.name
                                ? item.id < 0
                                  ? formatMessage({ id: item.name })
                                  : item.name
                                : ""
                            );
                          }}
                        >
                          <img src={connected} alt="" />
                          <span>
                            {item.id < 0
                              ? formatMessage({ id: item.name })
                              : item.name}{" "}
                            ({tabsTotals[tabIDToType(item.id)!]})
                          </span>
                        </li>
                      );
                    })
                )}
            </ul>*/}
          </ScrollableArea>
        </div>
      )}
    </div>
  );
}

export { MinersTabSelect };
